import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Store, Camera, Save, Globe, Phone, Mail, MapPin, Hash, MessageCircle, Send, Instagram, Facebook, Plus, Trash2, Bot } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import HomeVetrinaManager from '../components/HomeVetrinaManager';
import BrandKitManager from '../components/BrandKitManager';
import CardTemplateManager from '../components/CardTemplateManager';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopSettingsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const logoRef = useRef(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoSrc, setLogoSrc] = useState(null);
  const [coverSrc, setCoverSrc] = useState(null);
  const coverRef = useRef(null);

  const getHeaders = () => {
    const headers = {};
    if (user?.role === 'superadmin') {
      const activeShop = localStorage.getItem('phv_active_shop');
      if (activeShop) headers['X-Shop-Id'] = activeShop;
    }
    return headers;
  };

  useEffect(() => {
    if (authLoading) return; // wait for auth hydration
    if (!user || !['admin', 'superadmin'].includes(user.role)) {
      navigate('/account');
      return;
    }
    fetchShop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchShop = async () => {
    try {
      const { data } = await axios.get(`${API}/shops/mine`, { withCredentials: true, headers: getHeaders() });
      setShop(data);
      if (data.logo_path) {
        try {
          const res = await axios.get(`${API}/files/${data.logo_path}`, { responseType: 'blob' });
          setLogoSrc(URL.createObjectURL(res.data));
        } catch {}
      }
      if (data.cover_path) {
        try {
          const res = await axios.get(`${API}/files/${data.cover_path}`, { responseType: 'blob' });
          setCoverSrc(URL.createObjectURL(res.data));
        } catch {}
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!shop) return;
    setSaving(true);
    try {
      await axios.put(`${API}/shops/mine`, {
        name: shop.name,
        description: shop.description,
        address: shop.address,
        phone: shop.phone,
        email: shop.email,
        vat_number: shop.vat_number,
        social_links: shop.social_links
      }, { withCredentials: true, headers: getHeaders() });
      toast.success('Impostazioni salvate!');
    } catch (e) {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/shops/mine/logo`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Logo aggiornato!');
      const reader = new FileReader();
      reader.onload = () => setLogoSrc(reader.result);
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error('Errore upload logo');
    }
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/shops/mine/cover`, formData, {
        withCredentials: true,
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Immagine di copertina aggiornata!');
      const reader = new FileReader();
      reader.onload = () => setCoverSrc(reader.result);
      reader.readAsDataURL(file);
    } catch (e) {
      toast.error('Errore upload copertina');
    }
  };

  const updateField = (field, value) => {
    setShop(prev => ({ ...prev, [field]: value }));
  };

  const updateSocial = (key, value) => {
    setShop(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [key]: value }
    }));
  };

  if (loading) {
    return (
      <div className="pb-safe px-4 pt-4 space-y-4">
        {[1,2,3,4].map(i => <div key={i} className="h-16 skeleton-shimmer rounded-xl" />)}
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="pb-safe px-4 pt-8 text-center">
        <Store size={40} className="mx-auto text-[#877E78] mb-3" />
        <h3 className="text-lg font-medium text-[#2A2624] mb-2" style={{ fontFamily: 'Outfit' }}>
          {user?.role === 'superadmin' ? 'Seleziona un negozio' : 'Nessun negozio trovato'}
        </h3>
        <p className="text-sm text-[#877E78] mb-4">
          {user?.role === 'superadmin'
            ? 'Vai al Pannello SuperAdmin e clicca "Entra" su un negozio per gestirlo.'
            : 'Il tuo negozio non è ancora configurato.'}
        </p>
        {user?.role === 'superadmin' && (
          <Button
            data-testid="goto-superadmin-from-settings"
            onClick={() => navigate('/superadmin')}
            className="bg-[#C25934] hover:bg-[#A64B2B] text-white rounded-full px-8 h-12"
          >
            Vai al Pannello
          </Button>
        )}
      </div>
    );
  }

  return (
    <div data-testid="shop-settings-page" className="pb-safe px-4 pt-4">
      <h1 className="text-2xl font-semibold text-[#2A2624] mb-5" style={{ fontFamily: 'Outfit' }}>
        Impostazioni Negozio
      </h1>

      {/* Logo */}
      <div className="flex items-center gap-4 mb-4">
        <div
          onClick={() => logoRef.current?.click()}
          className="w-20 h-20 rounded-2xl bg-[#F0EBE1] border-2 border-dashed border-[#C25934]/30 flex items-center justify-center cursor-pointer overflow-hidden hover:border-[#C25934]/60 transition-colors"
        >
          {logoSrc ? (
            <img src={logoSrc} alt="Logo" className="w-full h-full object-cover" />
          ) : (
            <Camera size={28} className="text-[#C25934]/50" />
          )}
          <input ref={logoRef} type="file" accept="image/*" onChange={uploadLogo} className="hidden" data-testid="logo-input" />
        </div>
        <div>
          <h3 className="font-medium text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>{shop.name || 'Il tuo negozio'}</h3>
          <p className="text-xs text-[#877E78]">Clicca per cambiare il logo</p>
        </div>
      </div>

      {/* Cover Image */}
      <div
        onClick={() => coverRef.current?.click()}
        className="relative h-36 rounded-2xl bg-[#F0EBE1] border-2 border-dashed border-[#C25934]/30 mb-6 cursor-pointer overflow-hidden hover:border-[#C25934]/60 transition-colors group"
      >
        {coverSrc ? (
          <>
            <img src={coverSrc} alt="Copertina" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <span className="text-white text-sm font-medium">Cambia Copertina</span>
            </div>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <Camera size={28} className="text-[#C25934]/50" />
            <p className="text-xs text-[#877E78] mt-1">Immagine di Copertina</p>
            <p className="text-[10px] text-[#877E78]">Visibile nella home del negozio e nelle anteprime social</p>
          </div>
        )}
        <input ref={coverRef} type="file" accept="image/*" onChange={uploadCover} className="hidden" data-testid="cover-input" />
      </div>

      <div className="space-y-4">
        {/* Basic Info */}
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Informazioni</h3>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <Store size={14} /> Nome Negozio
            </label>
            <Input
              data-testid="shop-name"
              value={shop.name || ''}
              onChange={e => updateField('name', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="Nome della tua attività"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <Globe size={14} /> Descrizione
            </label>
            <textarea
              data-testid="shop-description"
              value={shop.description || ''}
              onChange={e => updateField('description', e.target.value)}
              rows={3}
              className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#C25934]"
              placeholder="Descrivi la tua attività..."
            />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Contatti</h3>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <Phone size={14} /> Telefono
            </label>
            <Input
              data-testid="shop-phone"
              value={shop.phone || ''}
              onChange={e => updateField('phone', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="+39 333 1234567"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <Mail size={14} /> Email
            </label>
            <Input
              data-testid="shop-email"
              value={shop.email || ''}
              onChange={e => updateField('email', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="info@tuonegozio.it"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <MapPin size={14} /> Indirizzo
            </label>
            <Input
              data-testid="shop-address"
              value={shop.address || ''}
              onChange={e => updateField('address', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="Via Roma 1, 00100 Roma"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
              <Hash size={14} /> Partita IVA
            </label>
            <Input
              data-testid="shop-vat"
              value={shop.vat_number || ''}
              onChange={e => updateField('vat_number', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="IT12345678901"
            />
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Categorie Prodotti</h3>
          <p className="text-xs text-[#877E78]">Personalizza le categorie del tuo negozio. L'AI assegnerà automaticamente i prodotti.</p>
          <div className="space-y-2">
            {(shop.categories || []).map((cat, idx) => (
              <div key={idx} className="flex gap-2 items-center">
                <Input
                  data-testid={`category-${idx}`}
                  value={cat}
                  onChange={e => {
                    const cats = [...(shop.categories || [])];
                    cats[idx] = e.target.value;
                    updateField('categories', cats);
                  }}
                  className="h-10 rounded-lg border-[#E8E3D9] text-sm flex-1"
                />
                <button
                  onClick={() => {
                    const cats = (shop.categories || []).filter((_, i) => i !== idx);
                    updateField('categories', cats);
                  }}
                  className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              data-testid="add-category-btn"
              onClick={() => updateField('categories', [...(shop.categories || []), ''])}
              className="flex items-center gap-1.5 text-xs text-[#C25934] font-medium hover:text-[#A64B2B]"
            >
              <Plus size={14} /> Aggiungi Categoria
            </button>
          </div>
        </div>

        {/* Social Links */}
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider">Social & Canali</h3>
          {[
            { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, placeholder: '+39 333 1234567', color: '#25D366' },
            { key: 'telegram', label: 'Telegram', icon: Send, placeholder: '@tuocanale o link gruppo', color: '#0088cc' },
            { key: 'facebook', label: 'Facebook', icon: Facebook, placeholder: 'Link pagina/gruppo Facebook', color: '#1877F2' },
            { key: 'instagram', label: 'Instagram', icon: Instagram, placeholder: '@tuoaccount', color: '#E4405F' },
          ].map(s => (
            <div key={s.key}>
              <label className="text-sm font-medium text-[#5C554F] flex items-center gap-1.5">
                <s.icon size={14} style={{ color: s.color }} /> {s.label}
              </label>
              <Input
                data-testid={`shop-social-${s.key}`}
                value={shop.social_links?.[s.key] || ''}
                onChange={e => updateSocial(s.key, e.target.value)}
                className="h-12 rounded-xl border-[#E8E3D9] mt-1"
                placeholder={s.placeholder}
              />
            </div>
          ))}
        </div>

        {/* Telegram Bot */}
        <div className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-3">
          <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider flex items-center gap-2">
            <Bot size={16} className="text-[#0088cc]" /> Telegram Bot
          </h3>
          <p className="text-xs text-[#877E78]">
            Configura il bot per inviare automaticamente i nuovi prodotti nei tuoi gruppi Telegram.
            Crea un bot con <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-[#0088cc] underline">@BotFather</a> e inserisci il token.
          </p>
          <div>
            <label className="text-sm font-medium text-[#5C554F]">Token Bot</label>
            <Input
              data-testid="telegram-bot-token"
              value={shop.telegram_bot_token || ''}
              onChange={e => updateField('telegram_bot_token', e.target.value)}
              className="h-12 rounded-xl border-[#E8E3D9] mt-1"
              placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-[#5C554F]">Chat/Gruppo ID (uno per riga)</label>
            <textarea
              data-testid="telegram-chat-ids"
              value={(shop.telegram_chat_ids || []).join('\n')}
              onChange={e => updateField('telegram_chat_ids', e.target.value.split('\n').map(s => s.trim()).filter(Boolean))}
              rows={3}
              className="w-full mt-1 px-3 py-2 rounded-xl border border-[#E8E3D9] bg-white text-sm resize-none outline-none focus:ring-2 focus:ring-[#0088cc]"
              placeholder="-1001234567890&#10;-1009876543210"
            />
            <p className="text-[10px] text-[#877E78] mt-1">Aggiungi il bot al gruppo, poi usa @getidsbot per ottenere l'ID del gruppo</p>
          </div>
          <Button
            data-testid="test-telegram-btn"
            onClick={async () => {
              try {
                const { data } = await axios.post(`${API}/telegram/test`, {}, { withCredentials: true, headers: getHeaders() });
                toast.success(data.message);
              } catch (e) {
                const msg = e.response?.data?.detail || 'Errore test';
                toast.error(typeof msg === 'string' ? msg : 'Errore');
              }
            }}
            variant="outline"
            className="w-full h-10 rounded-xl border-[#0088cc]/30 text-[#0088cc] hover:bg-[#0088cc]/5"
          >
            <Send size={14} className="mr-1.5" /> Invia Messaggio di Test
          </Button>
        </div>

        {/* HOME VETRINA — Banner + Social + Newsletter */}
        <HomeVetrinaManager getHeaders={getHeaders} shop={shop} onShopRefresh={fetchShop} />

        {/* CUSTOM DOMAIN — Dominio personalizzato del negozio */}
        <CustomDomainManager shop={shop} onShopRefresh={fetchShop} />

        {/* BRAND KIT — Logo + Scene refs + Prompt AI */}
        <BrandKitManager getHeaders={getHeaders} />

        {/* CARD TEMPLATE — Personalizzazione grafica card prodotto */}
        <CardTemplateManager getHeaders={getHeaders} />

        {/* PAYMENT SETTINGS */}
        <PaymentSettingsCard getHeaders={getHeaders} />

        {/* Save */}
        <Button
          data-testid="save-settings-btn"
          onClick={saveSettings}
          disabled={saving}
          className="w-full h-14 rounded-full bg-[#C25934] hover:bg-[#A64B2B] text-white text-base font-semibold"
          style={{ fontFamily: 'Outfit' }}
        >
          <Save size={20} className="mr-2" />
          {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
        </Button>
      </div>
    </div>
  );
}

function PaymentSettingsCard({ getHeaders }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecrets, setShowSecrets] = useState(false);

  // Editable form state
  const [stripeEnabled, setStripeEnabled] = useState(false);
  const [stripePub, setStripePub] = useState('');
  const [stripeSec, setStripeSec] = useState('');
  const [stripeWeb, setStripeWeb] = useState('');
  const [stripeKlarnaEnabled, setStripeKlarnaEnabled] = useState(true);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [paypalCid, setPaypalCid] = useState('');
  const [paypalSecret, setPaypalSecret] = useState('');
  const [paypalMode, setPaypalMode] = useState('sandbox');
  const [bankEnabled, setBankEnabled] = useState(false);
  const [iban, setIban] = useState('');
  const [holder, setHolder] = useState('');
  const [bankInst, setBankInst] = useState('');
  const [codEnabled, setCodEnabled] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/shops/mine/payment-settings`, { withCredentials: true, headers: getHeaders() });
      setData(d);
      setStripeEnabled(d.stripe.enabled);
      setStripePub(d.stripe.publishable_key || '');
      setStripeKlarnaEnabled(d.stripe.klarna_enabled !== false);
      setPaypalEnabled(d.paypal.enabled);
      setPaypalCid(d.paypal.client_id || '');
      setPaypalMode(d.paypal.mode || 'sandbox');
      setBankEnabled(d.bank_transfer.enabled);
      setIban(d.bank_transfer.iban || '');
      setHolder(d.bank_transfer.holder || '');
      setBankInst(d.bank_transfer.instructions || '');
      setCodEnabled(d.cash_on_delivery.enabled);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        stripe: {
          enabled: stripeEnabled,
          publishable_key: stripePub,
          // null = keep existing, "" = clear, value = update
          secret_key: stripeSec || null,
          webhook_secret: stripeWeb || null,
          klarna_enabled: stripeKlarnaEnabled,
        },
        paypal: {
          enabled: paypalEnabled,
          client_id: paypalCid,
          secret: paypalSecret || null,
          mode: paypalMode,
        },
        bank_transfer: {
          enabled: bankEnabled,
          iban,
          holder,
          instructions: bankInst,
        },
        cash_on_delivery: { enabled: codEnabled },
      };
      await axios.put(`${API}/shops/mine/payment-settings`, body, { withCredentials: true, headers: getHeaders() });
      toast.success('Impostazioni pagamenti salvate');
      setStripeSec(''); setStripeWeb(''); setPaypalSecret('');
      load();
    } catch {
      toast.error('Errore salvataggio pagamenti');
    } finally { setSaving(false); }
  };

  if (loading || !data) {
    return <div className="h-32 skeleton-shimmer rounded-xl" />;
  }

  return (
    <div data-testid="payment-settings-card" className="bg-white rounded-xl border border-[#E8E3D9] p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-[#2A2624] uppercase tracking-wider flex items-center gap-2">
          💳 Ricezione Pagamenti
        </h3>
        <button
          data-testid="toggle-show-secrets"
          onClick={() => setShowSecrets(s => !s)}
          className="text-[10px] text-[#877E78] hover:text-[#5C554F]"
        >
          {showSecrets ? 'Nascondi chiavi' : 'Mostra/modifica chiavi'}
        </button>
      </div>
      <p className="text-xs text-[#877E78]">
        Configura come incassare dai clienti. Le chiavi sono salvate solo nel tuo negozio.
      </p>

      {/* STRIPE */}
      <div className="rounded-xl border border-[#E8E3D9] p-3 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="stripe-enabled" checked={stripeEnabled} onChange={e => setStripeEnabled(e.target.checked)} className="w-4 h-4 rounded border-[#C25934] accent-[#635BFF]" />
          <span className="font-semibold text-[#2A2624]">Stripe (carte di credito)</span>
          {data.stripe.has_secret_key && <span className="text-[9px] bg-[#4A6B53]/10 text-[#4A6B53] px-1.5 py-0.5 rounded-full">CHIAVE SALVATA</span>}
        </label>
        {stripeEnabled && (
          <div className="space-y-2 pl-6">
            <Input data-testid="stripe-pub-key" value={stripePub} onChange={e => setStripePub(e.target.value)} placeholder="pk_live_... oppure pk_test_..." className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
            {showSecrets && (
              <>
                <Input data-testid="stripe-secret-key" type="password" value={stripeSec} onChange={e => setStripeSec(e.target.value)} placeholder={data.stripe.has_secret_key ? `Lascia vuoto per mantenere · ${data.stripe.secret_key_masked}` : "sk_live_... oppure sk_test_..."} className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
                <Input data-testid="stripe-webhook-secret" type="password" value={stripeWeb} onChange={e => setStripeWeb(e.target.value)} placeholder={data.stripe.has_webhook_secret ? `Lascia vuoto per mantenere · ${data.stripe.webhook_secret_masked}` : "whsec_... (webhook)"} className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
              </>
            )}
            <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer" className="text-[10px] text-[#0088cc] hover:underline">Apri dashboard Stripe →</a>

            {/* Klarna sotto-opzione (paga in 3 rate) */}
            <div className="mt-2 pt-2 border-t border-[#E8E3D9]">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="stripe-klarna-enabled"
                  checked={stripeKlarnaEnabled}
                  onChange={e => setStripeKlarnaEnabled(e.target.checked)}
                  className="w-4 h-4 rounded border-[#C25934] accent-[#FFA8CD] mt-0.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold text-[#2A2624]">Klarna · Paga in 3 rate</span>
                    <span className="text-[9px] bg-[#FFA8CD]/20 text-[#B4477A] px-1.5 py-0.5 rounded-full font-semibold">NUOVO</span>
                  </div>
                  <p className="text-[10px] text-[#6B645B] mt-0.5">
                    Mostra ai tuoi clienti l'opzione "Paga in 3 rate senza interessi" nel checkout. Devi attivare Klarna anche sulla tua{' '}
                    <a href="https://dashboard.stripe.com/settings/payment_methods" target="_blank" rel="noreferrer" className="text-[#0088cc] hover:underline">dashboard Stripe</a>.
                  </p>
                </div>
              </label>
            </div>
          </div>
        )}
      </div>

      {/* PAYPAL */}
      <div className="rounded-xl border border-[#E8E3D9] p-3 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="paypal-enabled" checked={paypalEnabled} onChange={e => setPaypalEnabled(e.target.checked)} className="w-4 h-4 rounded border-[#C25934] accent-[#003087]" />
          <span className="font-semibold text-[#2A2624]">PayPal</span>
          {data.paypal.has_secret && <span className="text-[9px] bg-[#4A6B53]/10 text-[#4A6B53] px-1.5 py-0.5 rounded-full">CHIAVE SALVATA</span>}
        </label>
        {paypalEnabled && (
          <div className="space-y-2 pl-6">
            <Input data-testid="paypal-client-id" value={paypalCid} onChange={e => setPaypalCid(e.target.value)} placeholder="Client ID PayPal" className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
            {showSecrets && (
              <Input data-testid="paypal-secret" type="password" value={paypalSecret} onChange={e => setPaypalSecret(e.target.value)} placeholder={data.paypal.has_secret ? `Lascia vuoto per mantenere · ${data.paypal.secret_masked}` : "Secret PayPal"} className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
            )}
            <select data-testid="paypal-mode" value={paypalMode} onChange={e => setPaypalMode(e.target.value)} className="h-10 text-sm rounded-lg border border-[#E8E3D9] bg-white px-2 w-full">
              <option value="sandbox">Sandbox (test)</option>
              <option value="live">Live (produzione)</option>
            </select>
            <a href="https://developer.paypal.com/dashboard/applications/sandbox" target="_blank" rel="noreferrer" className="text-[10px] text-[#0088cc] hover:underline">Apri dashboard PayPal →</a>
          </div>
        )}
      </div>

      {/* BANK TRANSFER */}
      <div className="rounded-xl border border-[#E8E3D9] p-3 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="bank-enabled" checked={bankEnabled} onChange={e => setBankEnabled(e.target.checked)} className="w-4 h-4 rounded border-[#C25934] accent-[#4A6B53]" />
          <span className="font-semibold text-[#2A2624]">Bonifico Bancario</span>
        </label>
        {bankEnabled && (
          <div className="space-y-2 pl-6">
            <Input data-testid="bank-iban" value={iban} onChange={e => setIban(e.target.value)} placeholder="IT60X0542811101000000123456" className="h-10 text-sm rounded-lg border-[#E8E3D9] uppercase" />
            <Input data-testid="bank-holder" value={holder} onChange={e => setHolder(e.target.value)} placeholder="Intestatario (es: Casa & Stile SRL)" className="h-10 text-sm rounded-lg border-[#E8E3D9]" />
            <textarea data-testid="bank-instructions" value={bankInst} onChange={e => setBankInst(e.target.value)} rows={2} placeholder="Istruzioni per il cliente (causale, tempi)" className="w-full px-3 py-2 text-sm rounded-lg border border-[#E8E3D9] resize-none outline-none focus:ring-2 focus:ring-[#4A6B53]" />
          </div>
        )}
      </div>

      {/* CASH ON DELIVERY */}
      <div className="rounded-xl border border-[#E8E3D9] p-3">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" data-testid="cod-enabled" checked={codEnabled} onChange={e => setCodEnabled(e.target.checked)} className="w-4 h-4 rounded border-[#C25934] accent-[#D98324]" />
          <span className="font-semibold text-[#2A2624]">Contrassegno (pagamento alla consegna)</span>
        </label>
      </div>

      <Button
        data-testid="save-payments-btn"
        onClick={save}
        disabled={saving}
        className="w-full h-11 rounded-full bg-[#4A6B53] hover:bg-[#3a5643] text-white font-medium"
      >
        {saving ? 'Salvataggio...' : '💾 Salva impostazioni pagamenti'}
      </Button>
    </div>
  );
}


function CustomDomainManager({ shop, onShopRefresh }) {
  const [info, setInfo] = useState(null); // {slug, custom_domain, custom_domain_status, eligibility, cname_target}
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadInfo = async () => {
    try {
      const { data } = await axios.get(`${API}/shops/mine/custom-domain`, { withCredentials: true });
      setInfo(data);
      setDomain(data?.custom_domain || '');
    } catch (e) {
      console.error('Errore caricamento eligibility', e);
    }
  };

  useEffect(() => { loadInfo(); /* eslint-disable-next-line */ }, []);

  // Auto-poll while domain is pending validation (every 20s)
  useEffect(() => {
    if (!info?.cloudflare_hostname_id) return;
    if (info?.custom_domain_status === 'active' && info?.cloudflare_ssl_status === 'active') return;
    const t = setInterval(() => { loadInfo(); }, 20000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [info?.cloudflare_hostname_id, info?.custom_domain_status, info?.cloudflare_ssl_status]);

  const slug = info?.slug || shop?.slug || 'il-tuo-negozio';
  const cnameTarget = info?.cname_target || 'proxy.photovendo.com';
  const targetUrl = `https://photovendo.com/negozio/${slug}`;
  const eligibility = info?.eligibility || { eligible: false, source: null, addon_price: 5.0, plan_name: null };
  const status = info?.custom_domain_status || (info?.custom_domain ? 'pending' : null);
  const currentDomain = info?.custom_domain || '';

  const copyTarget = async (value, msg = 'URL copiato') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(msg);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Impossibile copiare');
    }
  };

  const submit = async () => {
    const cleaned = (domain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/+$/, '');
    if (!cleaned) return;
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/shops/mine/custom-domain`, { custom_domain: cleaned }, { withCredentials: true });
      toast.success(data.message || 'Richiesta inviata');
      await loadInfo();
      onShopRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Rimuovere il dominio personalizzato?')) return;
    setSaving(true);
    try {
      await axios.put(`${API}/shops/mine/custom-domain`, { custom_domain: '' }, { withCredentials: true });
      toast.success('Dominio rimosso');
      setDomain('');
      await loadInfo();
      onShopRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    } finally {
      setSaving(false);
    }
  };

  const statusBadge = {
    pending: { text: '⏳ In attesa di attivazione (entro 24h)', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
    active: { text: '✅ Dominio attivo', cls: 'bg-[#4A6B53]/15 text-[#4A6B53] border-[#4A6B53]/40' },
  };

  // PAYWALL: shop is not eligible (Starter without addon)
  if (info && !eligibility.eligible) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-[#E8E3D9] space-y-3" data-testid="custom-domain-card">
        <div>
          <h3 className="text-base font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>🌐 Usa il tuo dominio personale</h3>
          <p className="text-xs text-[#877E78] mt-1">
            Fai sì che i tuoi clienti vedano <code>iltuonegozio.it</code> nella barra del browser invece di photovendo.com.
          </p>
        </div>

        {/* Paywall card */}
        <div className="rounded-xl border-2 border-dashed border-[#C25934]/40 bg-gradient-to-br from-[#FFF6F0] to-white p-4 space-y-3" data-testid="custom-domain-paywall">
          <div className="flex items-center gap-2">
            <span className="text-2xl">🔒</span>
            <div>
              <div className="text-sm font-bold text-[#2A2624]">Feature Premium</div>
              <div className="text-[11px] text-[#877E78]">Inclusa nei piani Business e Enterprise</div>
            </div>
          </div>
          <div className="bg-white border border-[#E8E3D9] rounded-lg p-3 space-y-2">
            <p className="text-xs text-[#5C554F]"><b>Hai 2 modi per attivarla:</b></p>
            <div className="space-y-1.5 text-xs">
              <div className="flex items-start gap-2">
                <span className="text-[#4A6B53] font-bold">A.</span>
                <span>Passa al <b>piano Business</b> (€49/mese) → dominio personale incluso + Telegram bot + PayPal + Reverse Search</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#4A6B53] font-bold">B.</span>
                <span>Aggiungi l'<b>addon Dominio Personalizzato</b> al tuo piano attuale → solo <b>€{eligibility.addon_price?.toFixed(0)}/mese</b></span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              data-testid="custom-domain-upgrade-plan"
              onClick={() => window.open('/prezzi', '_blank')}
              className="flex-1 h-10 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white text-xs font-semibold"
            >
              Vedi piani →
            </Button>
            <Button
              data-testid="custom-domain-request-addon"
              variant="outline"
              onClick={() => {
                const subject = encodeURIComponent('Voglio attivare addon Dominio Personalizzato');
                const body = encodeURIComponent(`Ciao, vorrei attivare l'addon "Dominio Personalizzato" (€${eligibility.addon_price?.toFixed(0)}/mese) sul mio negozio "${shop?.name || ''}".\n\nGrazie!`);
                window.location.href = `mailto:team@photovendo.com?subject=${subject}&body=${body}`;
              }}
              className="flex-1 h-10 rounded-xl border-[#C25934] text-[#C25934] hover:bg-[#FFF6F0] text-xs font-semibold"
            >
              ✉️ Richiedi addon
            </Button>
          </div>
        </div>

        {/* Free fallback: 301 redirect */}
        <details className="bg-[#F8F5F0] rounded-xl p-3 border border-[#E8E3D9]">
          <summary className="text-sm font-semibold text-[#2A2624] cursor-pointer">💡 Alternativa gratuita: Redirect 301</summary>
          <div className="mt-3 text-xs text-[#5C554F] space-y-2">
            <p>Puoi comunque usare il tuo dominio configurando un <b>redirect 301</b> sul tuo registrar (es. Aruba). Il browser cambierà l'URL in photovendo.com, ma è gratis e funziona subito.</p>
            <div className="flex gap-2 items-center mt-2">
              <code className="flex-1 bg-white border border-[#E8E3D9] rounded-lg px-2 py-1.5 text-[11px] font-mono break-all">{targetUrl}</code>
              <Button
                onClick={() => copyTarget(targetUrl)}
                className="h-8 px-2 rounded-lg bg-[#C25934] hover:bg-[#A64B2B] text-white text-[11px]"
              >
                {copied ? '✓' : 'Copia'}
              </Button>
            </div>
            <p className="text-[11px] mt-2">Su Aruba: <i>Gestione Dominio → Redirect e sottodomini → Permanente (301)</i>. <b>NON usare "Mascheramento"</b>.</p>
          </div>
        </details>
      </div>
    );
  }

  // ELIGIBLE: full form with CNAME instructions
  return (
    <div className="bg-white rounded-2xl p-5 border border-[#E8E3D9] space-y-3" data-testid="custom-domain-card">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <h3 className="text-base font-bold text-[#2A2624]" style={{ fontFamily: 'Outfit' }}>🌐 Dominio personalizzato</h3>
          <p className="text-xs text-[#877E78] mt-1">
            Configura il tuo dominio (es. <code>casastile.it</code>) per il tuo negozio. URL visibile nella barra, SSL automatico.
          </p>
        </div>
        <span className="text-[10px] font-bold px-2 py-1 rounded-full bg-[#4A6B53]/15 text-[#4A6B53] whitespace-nowrap">
          {eligibility.source === 'plan' ? `✓ Incluso (${eligibility.plan_name || 'piano'})` : '✓ Addon attivo'}
        </span>
      </div>

      {(status || info?.cloudflare_hostname_id) && (
        <div className="rounded-lg border border-[#E8E3D9] bg-[#F8F5F0] p-3 space-y-2" data-testid="custom-domain-status-badge">
          {currentDomain && (
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs text-[#2A2624] truncate">{currentDomain}</span>
              {info?.custom_domain_status === 'active' && info?.cloudflare_ssl_status === 'active' ? (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#4A6B53]/20 text-[#4A6B53]">✅ ATTIVO</span>
              ) : (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">⏳ IN VERIFICA</span>
              )}
            </div>
          )}
          {/* Progress steps */}
          {info?.cloudflare_hostname_id && (
            <div className="space-y-1 text-[11px]">
              <div className="flex items-center gap-2">
                <span className={info?.cloudflare_status === 'active' ? 'text-[#4A6B53]' : 'text-amber-600'}>
                  {info?.cloudflare_status === 'active' ? '✅' : '⏳'}
                </span>
                <span className="text-[#5C554F]">Verifica DNS (CNAME)</span>
                {info?.cloudflare_status && info?.cloudflare_status !== 'active' && (
                  <span className="text-[#877E78] italic">— controlla il tuo DNS</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className={info?.cloudflare_ssl_status === 'active' ? 'text-[#4A6B53]' : info?.cloudflare_ssl_status === 'initializing' ? 'text-[#877E78]' : 'text-amber-600'}>
                  {info?.cloudflare_ssl_status === 'active' ? '✅' : '⏳'}
                </span>
                <span className="text-[#5C554F]">Generazione SSL automatico</span>
                {info?.cloudflare_ssl_status && info?.cloudflare_ssl_status !== 'active' && (
                  <span className="text-[#877E78] italic">— {info?.cloudflare_ssl_status}</span>
                )}
              </div>
            </div>
          )}
          {/* Verification errors from Cloudflare */}
          {info?.cloudflare_verification_errors?.length > 0 && (
            <div className="text-[11px] bg-red-50 border border-red-200 rounded p-2 text-red-700">
              <b>Errore verifica:</b> {info.cloudflare_verification_errors.join(' · ')}
            </div>
          )}
          {info?.cloudflare_hostname_id && info?.custom_domain_status !== 'active' && (
            <p className="text-[11px] text-[#877E78] italic">
              ⚡ La pagina si aggiorna automaticamente ogni 20 secondi. Di solito ci vogliono 1-15 minuti dopo aver configurato il CNAME.
            </p>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <Input
          data-testid="custom-domain-input"
          value={domain}
          onChange={e => setDomain(e.target.value)}
          placeholder="es. www.tuodominio.com"
          className="flex-1 h-11 rounded-xl border-[#E8E3D9] font-mono text-sm"
        />
        <Button
          data-testid="custom-domain-save"
          onClick={submit}
          disabled={saving || !domain.trim()}
          className="h-11 px-4 rounded-xl bg-[#C25934] hover:bg-[#A64B2B] text-white font-medium"
        >
          {saving ? '...' : (currentDomain ? 'Aggiorna' : 'Richiedi')}
        </Button>
      </div>

      {currentDomain && (
        <Button
          data-testid="custom-domain-clear"
          variant="ghost"
          onClick={clear}
          className="h-9 text-xs text-red-500 hover:bg-red-50"
        >
          Rimuovi dominio
        </Button>
      )}

      <details className="bg-[#FFF8F0] rounded-xl p-3 border border-[#F0E0CC]" open={!currentDomain}>
        <summary className="text-sm font-semibold text-[#2A2624] cursor-pointer">📋 Come configurare il DNS</summary>
        <div className="mt-3 text-xs text-[#5C554F] space-y-3">
          <div>
            <p className="font-semibold text-[#2A2624] mb-1">1. Entra nel pannello DNS del tuo provider</p>
            <p>Aruba, GoDaddy, Cloudflare, Register.it, Netsons, ecc. (di solito sotto "Gestione DNS" o "DNS Records").</p>
          </div>

          <div>
            <p className="font-semibold text-[#2A2624] mb-1">2. Aggiungi questi record CNAME:</p>
            <div className="bg-white border border-[#E8E3D9] rounded-lg p-2 font-mono text-[11px] space-y-1.5">
              <div className="flex items-center gap-2">
                <span><b>Tipo:</b> CNAME · <b>Host:</b> @ (o vuoto) · <b>Valore:</b></span>
                <code className="bg-[#F0EBE1] px-1.5 py-0.5 rounded text-[10px]">{cnameTarget}</code>
              </div>
              <div className="flex items-center gap-2">
                <span><b>Tipo:</b> CNAME · <b>Host:</b> www · <b>Valore:</b></span>
                <code className="bg-[#F0EBE1] px-1.5 py-0.5 rounded text-[10px]">{cnameTarget}</code>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-[11px]">Valore da copiare:</span>
              <code className="flex-1 bg-white border border-[#E8E3D9] rounded px-2 py-1 text-[10px] font-mono break-all">{cnameTarget}</code>
              <Button
                onClick={() => copyTarget(cnameTarget, 'Valore CNAME copiato')}
                className="h-7 px-2 rounded-md bg-[#C25934] hover:bg-[#A64B2B] text-white text-[10px]"
              >
                {copied ? '✓' : 'Copia'}
              </Button>
            </div>
            <p className="text-[11px] mt-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
              💡 Aruba e alcuni provider non accettano CNAME su <code>@</code> (root). In quel caso usa <b>ALIAS</b> o un record <b>A</b> verso l'IP che ti forniremo dopo la richiesta.
            </p>
          </div>

          <div>
            <p className="font-semibold text-[#2A2624] mb-1">3. Inserisci qui sopra il tuo dominio e clicca "Richiedi"</p>
            <p>L'amministratore PhotoVendo riceverà la richiesta e attiverà il dominio (entro 24h).</p>
          </div>

          <div>
            <p className="font-semibold text-[#2A2624] mb-1">4. Riceverai un'email di conferma</p>
            <p>Quando il dominio sarà attivo. Da quel momento i tuoi clienti potranno raggiungerti dal tuo dominio personale, con SSL automatico.</p>
          </div>

          <p className="text-[11px] italic mt-3">Problemi? <a href="mailto:team@photovendo.com" className="text-[#C25934] underline">team@photovendo.com</a></p>
        </div>
      </details>
    </div>
  );
}
